import { Bitmap, look, streetCar, studioPerson, T, type RGB, type StudioScene } from "@bd/art";
import { dateOf, floorSize, type Company, type GameState } from "@bd/sim";
import { Container, Sprite, type Texture } from "pixi.js";
import { tex, texLazy } from "./textures";

type Season = "winter" | "spring" | "summer" | "autumn";

/** Town ground that runs on past the edge of the map, lined up with its tiles, so the screen never ends in black. */
/** Past the edge of the town: fields in the season's colours (snow, fresh green, summer grass, stubble), not grey paving. */
const FIELD: Record<string, [[number, number, number], [number, number, number]]> = {
  winter: [[212, 220, 228], [202, 210, 220]],
  spring: [[112, 156, 88], [104, 148, 82]],
  summer: [[118, 150, 76], [110, 142, 70]],
  autumn: [[156, 140, 84], [146, 130, 78]],
};

export function groundTexture(season = "summer"): Texture {
  // one iso tile is 24 × 12 screen pixels; the field alternates softly per tile, which repeats every 24 × 12
  const [c0, c1] = FIELD[season] ?? FIELD.summer;
  const b = new Bitmap(24, 12);
  for (let y = 0; y < 12; y++) for (let x = 0; x < 24; x++) {
    const ux = (y + x / 2) / 2;
    const uy = (y - x / 2) / 2;
    const odd = (Math.floor(ux / 6) + Math.floor(uy / 6)) & 1;
    b.set(x, y, odd ? c1 : c0);
  }
  return tex(b, 7_700_001 + Object.keys(FIELD).indexOf(season));
}

/** Soft round shadow for clouds drifting over the town. */
function cloudBitmap(): Bitmap {
  const w = 160;
  const h = 80;
  const b = new Bitmap(w, h);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const dx = (x - w / 2) / (w / 2);
    const dy = (y - h / 2) / (h / 2);
    const lump = 0.18 * Math.sin(x * 0.09) * Math.cos(y * 0.13);
    const v = 1 - Math.sqrt(dx * dx + dy * dy) + lump;
    if (v > 0) b.set(x, y, [24, 32, 48], Math.round(Math.min(1, v * 1.6) * 255));
  }
  return b;
}

function birdBitmap(frame: number): Bitmap {
  const b = new Bitmap(7, 4);
  const c: [number, number, number] = [40, 40, 52];
  if (frame) {
    b.set(0, 0, c); b.set(1, 1, c); b.set(2, 2, c); b.set(3, 2, c); b.set(4, 2, c); b.set(5, 1, c); b.set(6, 0, c);
  } else {
    b.set(0, 2, c); b.set(1, 1, c); b.set(2, 1, c); b.set(3, 2, c); b.set(4, 1, c); b.set(5, 1, c); b.set(6, 2, c);
  }
  return b;
}

function flakeBitmap(kind: "snow" | "rain" | "leaf", n: number): Bitmap {
  if (kind === "rain") {
    const b = new Bitmap(1, 4);
    for (let y = 0; y < 4; y++) b.set(0, y, [180, 200, 230], 150);
    return b;
  }
  if (kind === "snow") {
    const b = new Bitmap(2, 2);
    b.rect(0, 0, 2, 2, [250, 252, 255], 230);
    return b;
  }
  const LEAF: [number, number, number][] = [[214, 130, 52], [196, 96, 44], [226, 176, 70]];
  const b = new Bitmap(3, 2);
  b.rect(0, 0, 3, 2, LEAF[n % 3]);
  return b;
}

/** What the town knows about you: the year, how famous you are, your colour, and whether a game of yours just came out. */
export interface TownInfo {
  year: number;
  fans: number;
  /** company colour, "#rrggbb" */
  color: string;
  /** one of your games came out in the last two weeks: its review score (0..10); otherwise null */
  launchScore: number | null;
  /** the main building's front wall, in unit y (the front door's path runs from here down to the pavement) */
  frontUy: number;
}

export function townInfo(game: GameState, co: Company): TownInfo {
  const fresh = co.products.filter((p) => p.launched && game.week - p.releaseWeek >= 0 && game.week - p.releaseWeek < 2);
  return {
    year: dateOf(game.week).year,
    fans: co.fans,
    color: co.color,
    launchScore: fresh.length ? Math.max(...fresh.map((p) => p.score)) : null,
    frontUy: floorSize(co).h * T,
  };
}

const clamp01 = (v: number) => Math.max(0, Math.min(1, v));

/**
 * How busy the street is. Fame is your fans on a log scale against what counts as famous that year
 * (about 300k fans in 1965, rising to about 60M by 2035; under 1,000 fans nobody's heard of you); the town itself also
 * fills up a little as the decades pass.
 * Walkers go from 2 per pavement (1965, nobody's heard of you) to 18 (a household name), cars from 1 to 9 per lane;
 * low graphics caps them at 8 and 4.
 */
export function townCrowd(year: number, fans: number, low = false) {
  const era = clamp01((year - 1965) / 70);
  const famous = 5.48 + 2.3 * era; // log10 of the fans that make you a household name
  const fame = clamp01((Math.log10(Math.max(0, fans) + 1) - 3) / (famous - 3));
  const f = fame ** 1.4;
  let walkers = Math.round(2 + 3 * era + 13 * f);
  let cars = Math.round(1 + 1.5 * era + 6.5 * f);
  if (low) {
    walkers = Math.min(walkers, 8);
    cars = Math.min(cars, 4);
  }
  // some of the people out walking carry one of your boxes once you have fans
  const carry = fans >= 1000 ? 0.04 + 0.22 * fame : 0;
  return { era, fame, walkers, cars, carry };
}

/** How long the queue outside your door is in launch week: longer for better reviews and more fans. */
export function queueLength(score: number, fame: number, low = false): number {
  const n = Math.round(3 + (Math.max(0, score) / 10) ** 2 * 6 + fame * 5);
  return Math.max(3, Math.min(low ? 6 : 14, n));
}

interface Walker { sprite: Sprite; ux: number; uy: number; walk: number; dir: 1 | -1; speed: number; lk: number; t: number; frame: number; carry: boolean; a: number; gone: boolean }
interface Car { sprite: Sprite; ux: number; uy: number; lane: number; dir: 1 | -1; speed: number; v: number; a: number; gone: boolean }
/** someone in the launch-week queue; slot 0 is at the door */
interface Queuer { sprite: Sprite; ux: number; uy: number; lk: number; t: number; frame: number; slot: number; state: "queue" | "enter" | "leave"; a: number; front: boolean }
/** someone coming out with your game: down the path, then off along the pavement */
interface Buyer { sprite: Sprite; ux: number; uy: number; lk: number; t: number; frame: number; leg: 0 | 1; a: number; walked: number }
interface Flyer { sprite: Sprite; x: number; y: number; vx: number; vy: number; t: number; frame: number }
interface Drift { sprite: Sprite; x: number; y: number; vx: number }
interface Flake { sprite: Sprite; x: number; y: number; vx: number; vy: number; spin: number }

const carCache = new Map<string, { t: Texture; ax: number; ay: number }>();
function carTex(year: number, seed: number, nose: 1 | -1) {
  const k = `${year}:${seed}:${nose}`;
  let c = carCache.get(k);
  if (!c) {
    const s = streetCar(year, seed, nose);
    c = { t: tex(s.img), ax: s.ax, ay: s.ay };
    carCache.set(k, c);
  }
  return c;
}

/** texture keys for people carrying a box: one block of 256 per company colour */
const colourKeys = new Map<string, number>();

function hexRgb(col: string): RGB {
  const m = /^#?([0-9a-f]{6})$/i.exec(col);
  const n = m ? parseInt(m[1], 16) : 0xe85848;
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/** queue spacing along the line, in units */
const STEP = 4.4;

/**
 * The town going about its day around your studio: people on the pavements, traffic, birds, cloud shadows and the season's weather.
 * The street fills up as you get famous; in launch week a queue forms at your door and people walk off with your box.
 * Cheap on purpose: at most a few dozen small sprites, updated at the view's frame rate.
 */
export class TownLife {
  walkers: Walker[] = [];
  cars: Car[] = [];
  queue: Queuer[] = [];
  buyers: Buyer[] = [];
  birds: Flyer[] = [];
  clouds: Drift[] = [];
  flakes: Flake[] = [];
  /** world-space layers: clouds over the town; weather lives in screen space */
  cloudLayer = new Container();
  weatherLayer = new Container();
  private seed: number;
  private scene: StudioScene | null = null;
  private layer: Container | null = null;
  private info: TownInfo = { year: 1965, fans: 0, color: "#E85848", launchScore: null, frontUy: 0 };
  private low = false;
  private clock = 0;
  private nextSpawn = 0;
  private nextServe = 0;
  private buyersDue: number[] = [];

  constructor(seed = 11) {
    this.seed = seed;
  }

  private rand(): number {
    this.seed = (this.seed + 0x6d2b79f5) | 0;
    let t = Math.imul(this.seed ^ (this.seed >>> 15), 1 | this.seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  /** The year, your fans and colour, and any launch this week. Cheap: call it on every game update. */
  setTown(info: TownInfo) {
    const was = this.info.launchScore;
    this.info = info;
    // the launch is over: the queue drifts away
    if (was !== null && info.launchScore === null) for (const q of this.queue) q.state = "leave";
  }

  /** Low graphics: a quieter street. */
  setLow(low: boolean) {
    this.low = low;
  }

  private crowd() {
    return townCrowd(this.info.year, this.info.fans, this.low);
  }

  /** Set up for a freshly built scene. People and cars go into the depth-sorted layer so they pass behind lamps and trees. */
  build(scene: StudioScene, sortLayer: Container, season: Season, week: number) {
    this.scene = scene;
    this.layer = sortLayer;
    // the same week always starts the same street (for screenshots)
    this.seed = 11 + week * 7919;
    // the scene rebuild may already have cleared the depth-sorted layer
    for (const s of [...this.walkers, ...this.cars, ...this.queue, ...this.buyers]) if (!s.sprite.destroyed) s.sprite.destroy();
    this.cloudLayer.removeChildren().forEach((c) => c.destroy());
    this.weatherLayer.removeChildren().forEach((c) => c.destroy());
    this.walkers = [];
    this.cars = [];
    this.queue = [];
    this.buyers = [];
    this.buyersDue = [];
    this.birds = [];
    this.clouds = [];
    this.flakes = [];
    const crowd = this.crowd();
    // people on both pavements, strolling either way
    scene.walks.forEach((walk, wi) => {
      for (let i = 0; i < crowd.walkers; i++) this.addWalker(wi, walk.x0 + ((walk.x1 - walk.x0) * (i + this.rand() * 0.8)) / crowd.walkers, 1);
    });
    // passing traffic on the street out front
    scene.lanes.forEach((lane, li) => {
      for (let i = 0; i < crowd.cars; i++) this.addCar(li, lane.x0 + ((lane.x1 - lane.x0) * (i + li * 0.5 + this.rand() * 0.3)) / crowd.cars, 1);
    });
    // launch week: the queue is already there
    if (this.info.launchScore !== null) {
      const n = queueLength(this.info.launchScore, crowd.fame, this.low);
      for (let i = 0; i < n; i++) this.addQueuer(i, false);
      this.nextServe = this.clock + 2 + this.rand() * 2;
    }
    // a few birds crossing the sky
    for (let i = 0; i < 5; i++) {
      const sp = new Sprite(texLazy(7_700_010, () => birdBitmap(0)));
      this.cloudLayer.addChild(sp);
      this.birds.push({ sprite: sp, x: this.rand() * scene.w, y: this.rand() * scene.h * 0.6, vx: 14 + this.rand() * 10, vy: (this.rand() - 0.5) * 3, t: this.rand(), frame: 0 });
    }
    // cloud shadows: big, soft, slow
    const ct = texLazy(7_700_020, cloudBitmap);
    for (let i = 0; i < 4; i++) {
      const sp = new Sprite(ct);
      sp.blendMode = "multiply";
      sp.alpha = 0.16;
      sp.scale.set(1.6 + this.rand() * 1.4);
      this.cloudLayer.addChild(sp);
      this.clouds.push({ sprite: sp, x: this.rand() * scene.w, y: this.rand() * scene.h, vx: 2.5 + this.rand() * 2 });
    }
    // the season's weather: snow in winter, leaves in autumn, a shower on some spring weeks
    const showery = season === "spring" && ((week * 2654435761) >>> 0) % 10 < 3;
    const kind = season === "winter" ? "snow" : season === "autumn" ? "leaf" : showery ? "rain" : null;
    if (kind) for (let i = 0; i < (kind === "rain" ? 140 : 90); i++) {
      const sp = new Sprite(texLazy(7_700_100 + (kind === "rain" ? 0 : kind === "snow" ? 1 : 2 + (i % 3)), () => flakeBitmap(kind, i)));
      this.weatherLayer.addChild(sp);
      const fall = kind === "rain" ? 260 + this.rand() * 80 : kind === "snow" ? 18 + this.rand() * 16 : 22 + this.rand() * 18;
      this.flakes.push({ sprite: sp, x: this.rand() * 2000, y: this.rand() * 1200, vx: kind === "rain" ? -30 : (this.rand() - 0.3) * 14, vy: fall, spin: kind === "leaf" ? (this.rand() - 0.5) * 4 : 0 });
    }
  }

  private personTex(lk: number, front: boolean, frame: number, carry = false): Texture {
    if (!carry) return texLazy(8_000_000 + lk * 13 + (front ? 0 : 5) + frame, () => studioPerson(look(lk), { view: front ? "front" : "back", pose: frame ? "walk" : "stand", frame }));
    const col = this.info.color;
    let ck = colourKeys.get(col);
    if (ck === undefined) {
      ck = colourKeys.size;
      colourKeys.set(col, ck);
    }
    return texLazy(8_200_000 + (ck % 3000) * 256 + (lk % 64) * 4 + (front ? 0 : 2) + frame, () =>
      studioPerson(look(lk), { view: front ? "front" : "back", pose: frame ? "walk" : "stand", frame, tucked: hexRgb(col) }));
  }

  private person(lk: number, front: boolean, carry: boolean, alpha: number): Sprite {
    const sp = new Sprite(this.personTex(lk, front, 0, carry));
    sp.anchor.set(0.5, 1);
    sp.alpha = alpha;
    this.layer!.addChild(sp);
    return sp;
  }

  private addWalker(wi: number, ux: number, alpha: number) {
    const walk = this.scene!.walks[wi];
    const lk = Math.floor(this.rand() * 64);
    const dir = this.rand() < 0.5 ? 1 : -1;
    const carry = this.rand() < this.crowd().carry + (this.info.launchScore !== null ? 0.15 : 0);
    const sprite = this.person(lk, dir > 0, carry, alpha);
    this.walkers.push({ sprite, ux, uy: walk.uy + (this.rand() - 0.5) * 3, walk: wi, dir, speed: 3 + this.rand() * 2.5, lk, t: this.rand(), frame: 0, carry, a: alpha, gone: false });
  }

  private addCar(li: number, ux: number, alpha: number) {
    const lane = this.scene!.lanes[li];
    const c = carTex(this.info.year, Math.floor(this.rand() * 64), lane.dir);
    const sprite = new Sprite(c.t);
    sprite.pivot.set(c.ax, c.ay);
    sprite.alpha = alpha;
    this.layer!.addChild(sprite);
    const speed = 9 + this.rand() * 5;
    this.cars.push({ sprite, ux, uy: lane.uy - 2.5, lane: li, dir: lane.dir, speed, v: speed, a: alpha, gone: false });
  }

  /** where the front door's path meets the building, and how the queue bends from the path onto the pavement */
  private slotPos(i: number): [number, number] {
    const sc = this.scene!;
    const pathX = sc.kerb.ux + 2 * T - 2;
    const top = this.info.frontUy + 2.5;
    const pave = sc.walks[0].uy - 2.6;
    const onPath = Math.max(1, Math.floor((pave - top) / STEP) + 1);
    if (i < onPath) return [pathX + (i & 1 ? 0.3 : -0.3), top + i * STEP];
    return [pathX + (i - onPath + 1) * STEP, pave + ((i & 1) - 0.5) * 0.4];
  }

  private addQueuer(slot: number, walkIn: boolean) {
    const lk = Math.floor(this.rand() * 64);
    const [sx, sy] = this.slotPos(slot);
    const sprite = this.person(lk, false, false, walkIn ? 0 : 1);
    this.queue.push({ sprite, ux: walkIn ? sx + 16 : sx, uy: sy, lk, t: this.rand(), frame: 0, slot, state: "queue", a: walkIn ? 0 : 1, front: false });
  }

  /** Keep the street at the size your fame calls for: a newcomer every so often, extras fade out. */
  private grow() {
    const sc = this.scene!;
    const crowd = this.crowd();
    if (this.clock < this.nextSpawn) return;
    this.nextSpawn = this.clock + 0.4;
    sc.walks.forEach((walk, wi) => {
      const live = this.walkers.filter((w) => w.walk === wi && !w.gone);
      if (live.length < crowd.walkers) this.addWalker(wi, walk.x0 + this.rand() * (walk.x1 - walk.x0), 0);
      else if (live.length > crowd.walkers) live[Math.floor(this.rand() * live.length)].gone = true;
    });
    sc.lanes.forEach((lane, li) => {
      const live = this.cars.filter((c) => c.lane === li && !c.gone);
      if (live.length < crowd.cars) {
        // join where there's a gap, not on top of another car
        const ux = lane.x0 + this.rand() * (lane.x1 - lane.x0);
        if (live.every((c) => Math.abs(c.ux - ux) > 20)) this.addCar(li, ux, 0);
      } else if (live.length > crowd.cars) live[Math.floor(this.rand() * live.length)].gone = true;
    });
    // launch week: people join the queue at the back
    if (this.info.launchScore !== null) {
      const waiting = this.queue.filter((q) => q.state === "queue");
      if (waiting.length < queueLength(this.info.launchScore, crowd.fame, this.low) && this.rand() < 0.6) this.addQueuer(waiting.length, true);
    }
  }

  /** The front of the queue goes in; a moment later someone comes out with your game. */
  private serve() {
    if (this.info.launchScore === null || this.clock < this.nextServe) return;
    this.nextServe = this.clock + 3.5 + this.rand() * 3;
    const head = this.queue.find((q) => q.state === "queue" && q.slot === 0);
    if (!head) return;
    const [hx, hy] = this.slotPos(0);
    if (Math.hypot(head.ux - hx, head.uy - hy) > 1) return; // still shuffling up
    head.state = "enter";
    for (const q of this.queue) if (q.state === "queue") q.slot--;
    this.buyersDue.push(this.clock + 2.5 + this.rand() * 1.5);
  }

  private spawnBuyers() {
    const sc = this.scene!;
    const due = this.buyersDue.filter((t) => t <= this.clock);
    if (!due.length) return;
    this.buyersDue = this.buyersDue.filter((t) => t > this.clock);
    for (let i = 0; i < due.length; i++) {
      const lk = Math.floor(this.rand() * 64);
      const sprite = this.person(lk, true, true, 0);
      this.buyers.push({ sprite, ux: sc.kerb.ux + 2 * T + 2.5, uy: this.info.frontUy + 1, lk, t: 0, frame: 0, leg: 0, a: 0, walked: 0 });
    }
  }

  /** step a sprite's walk cycle; frame 0 is standing */
  private stride(p: { t: number; frame: number }, moving: boolean, dt: number): boolean {
    p.t += dt;
    if (!moving) {
      if (p.frame === 0) return false;
      p.frame = 0;
      return true;
    }
    if (p.t <= 0.24) return false;
    p.t = 0;
    p.frame ^= 1;
    return true;
  }

  /** Move everything along. Screen size is for the weather, which falls in screen pixels. */
  tick(dt: number, screenW: number, screenH: number) {
    const sc = this.scene;
    if (!sc || !this.layer) return;
    this.clock += dt;
    this.grow();
    this.serve();
    this.spawnBuyers();
    const fade = (cur: number, gone: boolean) => (gone ? Math.max(0, cur - dt * 1.5) : Math.min(1, cur + dt * 1.5));
    for (const w of this.walkers) {
      w.ux += w.dir * w.speed * dt;
      const walk = sc.walks[w.walk] ?? sc.walks[0];
      if (w.ux > walk.x1) w.ux = walk.x0;
      if (w.ux < walk.x0) w.ux = walk.x1;
      if (this.stride(w, true, dt)) w.sprite.texture = this.personTex(w.lk, w.dir > 0, w.frame, w.carry);
      w.a = fade(w.a, w.gone);
      w.sprite.alpha = w.a;
      const [px, py] = sc.P(w.ux, w.uy, 0);
      w.sprite.position.set(Math.round(px), Math.round(py));
      w.sprite.zIndex = w.ux + w.uy + 0.4;
    }
    // traffic keeps its distance: a faster car slows behind a slower one
    for (const c of this.cars) {
      let gap = Infinity;
      let ahead: Car | null = null;
      for (const o of this.cars) {
        if (o === c || o.lane !== c.lane || o.gone) continue;
        const d = (o.ux - c.ux) * c.dir;
        if (d > 0 && d < gap) {
          gap = d;
          ahead = o;
        }
      }
      const want = ahead && gap < 16 ? Math.min(c.speed, ahead.v * (gap < 13 ? 0.8 : 1)) : c.speed;
      c.v += (want - c.v) * Math.min(1, dt * 3);
      c.ux += c.dir * c.v * dt;
      const lane = sc.lanes[c.lane];
      if (c.ux > lane.x1) c.ux = lane.x0;
      if (c.ux < lane.x0) c.ux = lane.x1;
      c.a = fade(c.a, c.gone);
      c.sprite.alpha = c.a;
      const [px, py] = sc.P(c.ux, c.uy, 0);
      c.sprite.position.set(Math.round(px), Math.round(py));
      c.sprite.zIndex = c.ux + c.uy + 6;
    }
    // the queue shuffles up to its slots, faces the door, and goes in one at a time
    for (const q of this.queue) {
      let tx: number;
      let ty: number;
      if (q.state === "enter") [tx, ty] = [sc.kerb.ux + 2 * T - 2, this.info.frontUy - 1];
      else if (q.state === "leave") [tx, ty] = [q.ux + 10, sc.walks[0].uy - 2.6];
      else [tx, ty] = this.slotPos(Math.max(0, q.slot));
      const dx = tx - q.ux;
      const dy = ty - q.uy;
      const d = Math.hypot(dx, dy);
      const moving = d > 0.3;
      if (moving) {
        const k = Math.min(1, (4 * dt) / d);
        q.ux += dx * k;
        q.uy += dy * k;
      }
      // walking off to the right faces the camera; everyone else looks towards the door
      const front = q.state === "leave";
      if (this.stride(q, moving, dt) || front !== q.front) {
        q.front = front;
        q.sprite.texture = this.personTex(q.lk, front, q.frame);
      }
      const out = q.state === "leave" || (q.state === "enter" && q.uy < this.info.frontUy + 1.2);
      q.a = fade(q.a, out);
      q.sprite.alpha = q.a;
      const [px, py] = sc.P(q.ux, q.uy, 0);
      q.sprite.position.set(Math.round(px), Math.round(py));
      q.sprite.zIndex = q.ux + q.uy + 0.4;
    }
    // out they come with the box: down the path, then off along the pavement
    for (const b of this.buyers) {
      const goal = sc.walks[0].uy + 0.8;
      let front = true;
      if (b.leg === 0) {
        b.uy += 4 * dt;
        if (b.uy >= goal) {
          b.uy = goal;
          b.leg = 1;
        }
      } else {
        b.ux -= 4 * dt;
        b.walked += 4 * dt;
        front = false;
      }
      if (this.stride(b, true, dt)) b.sprite.texture = this.personTex(b.lk, front, b.frame, true);
      b.a = fade(b.a, b.walked > 30);
      b.sprite.alpha = b.a;
      const [px, py] = sc.P(b.ux, b.uy, 0);
      b.sprite.position.set(Math.round(px), Math.round(py));
      b.sprite.zIndex = b.ux + b.uy + 0.4;
    }
    this.sweep();
    for (const b of this.birds) {
      b.x += b.vx * dt;
      b.y += b.vy * dt;
      if (b.x > sc.w + 20) {
        b.x = -20;
        b.y = this.rand() * sc.h * 0.6;
      }
      b.t += dt;
      if (b.t > 0.18) {
        b.t = 0;
        b.frame ^= 1;
        b.sprite.texture = texLazy(7_700_010 + b.frame, () => birdBitmap(b.frame));
      }
      b.sprite.position.set(Math.round(b.x), Math.round(b.y));
    }
    for (const c of this.clouds) {
      c.x += c.vx * dt;
      if (c.x > sc.w + 200) c.x = -400;
      c.sprite.position.set(Math.round(c.x), Math.round(c.y));
    }
    for (const f of this.flakes) {
      f.x += f.vx * dt;
      f.y += f.vy * dt;
      if (f.spin) f.vx = Math.sin(f.y * 0.03) * 16;
      if (f.y > screenH + 10) {
        f.y = -10;
        f.x = this.rand() * screenW;
      }
      if (f.x < -10) f.x = screenW + 5;
      if (f.x > screenW + 10) f.x = -5;
      f.sprite.position.set(Math.round(f.x), Math.round(f.y));
    }
  }

  /** drop whoever has faded out */
  private sweep() {
    const done = (s: { sprite: Sprite; a: number }, out: boolean) => {
      if (!out || s.a > 0) return false;
      if (!s.sprite.destroyed) s.sprite.destroy();
      return true;
    };
    if (this.walkers.some((w) => w.gone && w.a <= 0)) this.walkers = this.walkers.filter((w) => !done(w, w.gone));
    if (this.cars.some((c) => c.gone && c.a <= 0)) this.cars = this.cars.filter((c) => !done(c, c.gone));
    if (this.queue.some((q) => q.state !== "queue" && q.a <= 0)) this.queue = this.queue.filter((q) => !done(q, q.state !== "queue"));
    if (this.buyers.some((b) => b.walked > 30 && b.a <= 0)) this.buyers = this.buyers.filter((b) => !done(b, b.walked > 30));
  }
}
